import React from 'react'
import { Box, keyframes } from '@mui/material'
import { styled } from '@mui/material/styles'
import { DiscoverFeedItem } from '../../hooks/useDiscoverFeed'
import { DiscoverCard } from './DiscoverCard'

export interface DiscoverGridProps {
  items: DiscoverFeedItem[]
  loading?: boolean
  skeletonCount?: number
  onSelectItem?: (item: DiscoverFeedItem) => void
  onReport?: (item: DiscoverFeedItem) => void
}

const Grid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(262px, 1fr))',
  gap: 22,
})

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`

const Skeleton = styled(Box)({
  borderRadius: 17,
  overflow: 'hidden',
  background: 'var(--ds-canvas)',
  boxShadow: 'var(--ds-shadow-soft)',
  animation: `${pulse} 1.6s var(--ds-ease) infinite`,
})

const SkeletonCov = styled(Box)({ aspectRatio: '4 / 3', background: 'var(--ds-surface)' })

const SkeletonMeta = styled(Box)({ padding: '13px 15px 14px' })

const SkeletonLine = styled(Box)({ height: 12, borderRadius: 6, background: 'var(--ds-surface)' })

/** "Fresh on the wall" — Discover's uniform 4:3 card grid, light theme. */
export function DiscoverGrid({ items, loading, skeletonCount = 12, onSelectItem, onReport }: DiscoverGridProps) {
  if (loading) {
    return (
      <Grid>
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <Skeleton key={i}>
            <SkeletonCov />
            <SkeletonMeta>
              <SkeletonLine sx={{ width: '70%', mb: 1 }} />
              <SkeletonLine sx={{ width: '40%', height: 9 }} />
            </SkeletonMeta>
          </Skeleton>
        ))}
      </Grid>
    )
  }

  return (
    <Grid>
      {items.map((item, i) => (
        <DiscoverCard
          key={item.id}
          item={item}
          index={i}
          onClick={() => onSelectItem?.(item)}
          onReport={onReport ? () => onReport(item) : undefined}
        />
      ))}
    </Grid>
  )
}
